<template>
  <div class="summary-page">
    <!-- 页面头部 -->
    <PageHeader size="page" divider>
      <template #title>
        <el-button 
          type="primary" 
          :icon="ArrowLeft" 
          @click="goBack"
          class="back-button"
        >
          返回
        </el-button>
        <span>{{ pageTitle }}</span>
      </template>
      <template #actions>
        <el-button 
          type="success" 
          :icon="Download" 
          @click="handleDownloadExcelImages"
          :loading="downloadingImages"
          :disabled="!summaryData"
        >
          下载Excel图片包
        </el-button>
      </template>
    </PageHeader>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="5" animated />
    </div>

    <!-- 汇总内容 -->
    <div v-else-if="summaryData" class="detail-content">
      <!-- 基本信息卡片（只显示经营项目名称和编码） -->
      <el-card class="info-card" shadow="hover">
        <template #header> 
          <PageHeader size="card">
            <template #title>基本信息</template>
          </PageHeader>
        </template>
        <div class="info-tags-container">
          <div class="info-tags">
            <el-tag 
              type="success"
              size="large"
              class="info-tag"
            >
              <span class="tag-label">名称：</span>
              <span class="tag-value">{{ businessInfo?.name || '未知项目' }}</span>
            </el-tag>
            <el-tag 
              type="info"
              size="large"
              class="info-tag"
            >
              <span class="tag-label">编码：</span>
              <span class="tag-value">{{ businessInfo?.code || '无编码' }}</span>
            </el-tag>
          </div>
        </div>
      </el-card>

      <!-- 检测项目列表 -->
      <div v-if="(summaryData.检测原始数据检测项目集合 as any[])?.length > 0" class="projects-section">
        <el-card 
          v-for="(project, projectIndex) in (summaryData.检测原始数据检测项目集合 as any[])" 
          :key="(project as any).Id"
          class="project-card"
          shadow="hover"
        >
          <template #header>
            <PageHeader size="card">
              <template #title>{{ (project as any).名称 }}</template>
              <template #actions>
                <el-tag type="info" size="small">项目 {{ Number(projectIndex) + 1 }}</el-tag>
              </template>
            </PageHeader>
          </template>

          <!-- 特殊属性 -->
          <div v-if="((project as any).检测原始数据检测项目特殊属性集合 as any[])?.length > 0" class="special-attributes">
            <div class="special-attributes-list">
              <div 
                v-for="attr in ((project as any).检测原始数据检测项目特殊属性集合 as any[])" 
                :key="(attr as any).标题"
                class="attribute-item"
              >
                <span class="attribute-label">{{ (attr as any).标题 }}：</span>
                <span class="attribute-value">{{ (attr as any).内容 || '无' }}</span>
              </div>
            </div>
          </div>

          <!-- 详细数据主表 -->
          <div v-if="((project as any).检测原始数据详细数据主表集合 as any[])?.length > 0" class="detailed-data">
            <h4>详细数据</h4>
            <div 
              v-for="(mainData, mainIndex) in ((project as any).检测原始数据详细数据主表集合 as any[])" 
              :key="(mainData as any).Id"
              class="data-table-container"
            >
              <!-- 表格摘要信息 -->
              <div class="table-summary-header">
                <span class="table-title">数据表 {{ Number(mainIndex) + 1 }}</span>
                <span class="table-summary">{{ (mainData as any).显示摘要 }}</span>
                <div v-if="getOptionExampleImages(mainData).length > 0" class="option-example-images">
                  <div
                    v-for="(optionImage, optionImageIndex) in getOptionExampleImages(mainData)"
                    :key="`${optionImage.fileName}_${optionImageIndex}`"
                    class="option-example-image"
                  >
                    <div v-if="optionImage.loading" class="option-example-placeholder">
                      <el-icon class="is-loading"><Loading /></el-icon>
                      <span>加载中</span>
                    </div>
                    <div v-else-if="optionImage.error" class="option-example-placeholder">
                      <el-icon><Picture /></el-icon>
                      <span>加载失败</span>
                    </div>
                    <el-image
                      v-else
                      :src="optionImage.url"
                      fit="contain"
                      class="option-example-thumb"
                      :preview-src-list="getOptionExampleImages(mainData).filter(img => !img.error && !img.loading && img.url).map(img => img.url)"
                      :initial-index="getOptionExampleImages(mainData).filter(img => !img.error && !img.loading && img.url).findIndex(img => img.url === optionImage.url)"
                      :preview-teleported="true"
                      :hide-on-click-modal="true"
                    >
                      <template #placeholder>
                        <div class="option-example-placeholder">
                          <el-icon class="is-loading"><Loading /></el-icon>
                        </div>
                      </template>
                      <template #error>
                        <div class="option-example-placeholder">
                          <el-icon><Picture /></el-icon>
                        </div>
                      </template>
                    </el-image>
                  </div>
                </div>
              </div>
              
              <!-- 数据表格 -->
              <el-table 
                :data="(mainData as any).检测原始数据详细数据子表集合 as any[]"
                border
                size="small"
                class="data-table"
                scrollbar-always-on
              >
                <el-table-column prop="Header" label="行号" width="80" />
                <el-table-column 
                  v-for="(title, index) in ((project as any).检测原始数据检测数据格式描述?.横向标题 as string[])" 
                  :key="index"
                  :prop="`_${Number(index) + 1}`"
                  :label="getTransformedTitle(title, project as any, mainData as any)"
                  :width="120"
                  show-overflow-tooltip
                  :class-name="hasDividerLine(project as any, Number(index)) ? 'divider-column' : ''"
                />
              </el-table>

              <!-- 该主表的现场照片 -->
              <div v-if="getMainTableImages(mainData).length > 0" class="main-table-images">
                <h5>现场照片</h5>
                <div class="images-grid-small">
                  <div 
                    v-for="(image, imgIndex) in getMainTableImages(mainData)" 
                    :key="imgIndex"
                    class="image-item-small"
                  >
                    <!-- 加载失败的占位图 -->
                    <div v-if="image.loading" class="image-error-placeholder">
                      <el-icon class="is-loading"><Loading /></el-icon>
                      <span>加载中</span>
                    </div>
                    <div v-else-if="image.error" class="image-error-placeholder">
                      <el-icon><Picture /></el-icon>
                      <span>加载失败</span>
                    </div>
                    <!-- 正常图片 -->
                    <el-image
                      v-else
                      :src="image.url"
                      fit="cover"
                      class="thumbnail-image-small"
                      @click="openRemarkImagePreview(mainData, imgIndex)"
                    >
                      <template #error>
                        <div class="image-error-small">
                          <el-icon><Picture /></el-icon>
                        </div>
                      </template>
                      <template #placeholder>
                        <div class="image-loading-small">
                          <el-icon class="is-loading"><Loading /></el-icon>
                        </div>
                      </template>
                    </el-image>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <el-empty v-else description="暂无检测项目数据" />
    </div>

    <el-empty v-else description="暂无汇总数据" />

    <el-dialog
      v-model="remarkImagePreviewVisible"
      title="现场照片"
      width="min(860px, 92vw)"
      top="6vh"
      destroy-on-close
    >
      <div class="remark-image-preview-dialog">
        <div class="remark-image-preview-stage">
          <el-button
            v-if="remarkPreviewImages.length > 1"
            class="remark-preview-nav remark-preview-nav--previous"
            :icon="ArrowLeft"
            circle
            :disabled="remarkPreviewIndex === 0"
            aria-label="上一张"
            @click="changeRemarkPreviewImage(-1)"
          />
          <img
            v-if="activeRemarkPreviewImage"
            :src="activeRemarkPreviewImage.url"
            class="remark-image-preview-image"
            alt="现场照片"
          />
          <el-button
            v-if="remarkPreviewImages.length > 1"
            class="remark-preview-nav remark-preview-nav--next"
            :icon="ArrowRight"
            circle
            :disabled="remarkPreviewIndex >= remarkPreviewImages.length - 1"
            aria-label="下一张"
            @click="changeRemarkPreviewImage(1)"
          />
        </div>
        <div class="remark-image-preview-note">
          <span class="remark-image-preview-note-label">备注</span>
          <span class="remark-image-preview-note-value">{{ activeRemarkPreviewImage?.remark || '无' }}</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, ArrowRight, Picture, Loading, Download } from '@element-plus/icons-vue'
import PageHeader from '@/components/PageHeader.vue'
import { useDetailStore } from '@/store/detailStore'
import { useRouteTitle } from '@/utils/useRouteTitle'
import ParameterSettings from '@/network/apis/ParameterSettings'
import { list } from '@/network/index'

const router = useRouter()
const route = useRoute()
const detailStore = useDetailStore()
const pageTitle = useRouteTitle('原始数据汇总')

const loading = ref(false)
const downloadingImages = ref(false)
const summaryData = ref<Record<string, unknown> | null>(null)

interface DetailImageItem {
  fileName: string
  url: string
  remark?: string
  loading?: boolean
  error?: boolean
}

interface RemarkImageSource {
  fileName: string
  remark: string
}

// 从 store 获取经营项目信息
const businessInfo = computed(() => detailStore.getSummaryBusinessInfo())

// 备注图像列表（按主表ID分组）
const remarkImagesByMainTable = ref<Map<string, DetailImageItem[]>>(new Map())
const optionExampleImagesByMainTable = ref<Map<string, DetailImageItem[]>>(new Map())
// 图片请求在页面显示后仍可能继续执行，下载图片包必须等待这组任务结束。
let remarkImagesLoadingPromise: Promise<void> = Promise.resolve()
const remarkImagePreviewVisible = ref(false)
const remarkPreviewImages = ref<DetailImageItem[]>([])
const remarkPreviewIndex = ref(0)
const activeRemarkPreviewImage = computed(() => remarkPreviewImages.value[remarkPreviewIndex.value])

// 获取指定主表的图片列表
const getMainTableImages = (mainData: any) => {
  const mainTableId = mainData.Id || mainData.id
  return remarkImagesByMainTable.value.get(String(mainTableId)) || []
}

const openRemarkImagePreview = (mainData: any, imageIndex: number) => {
  const sourceImages = getMainTableImages(mainData)
  const targetImage = sourceImages[imageIndex]
  const previewImages = sourceImages.filter(image => !image.loading && !image.error && image.url)
  const previewIndex = previewImages.findIndex(image => image === targetImage)
  if (previewIndex < 0) return

  remarkPreviewImages.value = previewImages
  remarkPreviewIndex.value = previewIndex
  remarkImagePreviewVisible.value = true
}

const changeRemarkPreviewImage = (offset: number) => {
  const nextIndex = remarkPreviewIndex.value + offset
  if (nextIndex >= 0 && nextIndex < remarkPreviewImages.value.length) {
    remarkPreviewIndex.value = nextIndex
  }
}

const getOptionExampleImages = (mainData: any) => {
  const mainTableId = mainData.Id || mainData.id
  return optionExampleImagesByMainTable.value.get(String(mainTableId)) || []
}

// 获取转换后的标题（处理单位替换）
const getTransformedTitle = (title: string, project: any, mainData: any): string => {
  if (!title) return ''
  
  // 检查是否有单位替换配置
  const unitReplaceJSON = project.检测原始数据检测数据格式描述?.单位替换JSON
  if (!unitReplaceJSON) return title
  
  try {
    const unitReplaceMap = JSON.parse(unitReplaceJSON)
    const summary = mainData.显示摘要 || ''
    
    // 遍历替换规则
    for (const [key, value] of Object.entries(unitReplaceMap)) {
      if (summary.includes(key)) {
        return title.replace(/\([^)]*\)/, `(${value})`)
      }
    }
  } catch (error) {
    console.error('解析单位替换JSON失败:', error)
  }
  
  return title
}

// 判断是否有分隔线
const hasDividerLine = (project: any, columnIndex: number): boolean => {
  const dividerLines = project.检测原始数据检测数据格式描述?.分区线
  if (!dividerLines || !Array.isArray(dividerLines)) return false
  return dividerLines[columnIndex] === true
}

const toNonEmptyString = (value: unknown): string => {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

const normalizeRemarkImage = (source: unknown): RemarkImageSource | null => {
  if (typeof source === 'string') {
    const fileName = toNonEmptyString(source)
    return fileName ? { fileName, remark: '' } : null
  }

  if (Array.isArray(source)) {
    const fileName = toNonEmptyString(source[0])
    return fileName ? { fileName, remark: toNonEmptyString(source[1]) } : null
  }

  if (!source || typeof source !== 'object') return null
  const item = source as Record<string, unknown>
  const fileName = toNonEmptyString(
    item['文件名'] ?? item['fileName'] ?? item['名称'] ?? item['name'] ?? item['图片名称'] ?? item['图片']
  )
  if (!fileName) return null
  return {
    fileName,
    remark: toNonEmptyString(item['备注'] ?? item['remark'] ?? item['说明'] ?? item['内容'])
  }
}

const parseRemarkImages = (source: unknown): RemarkImageSource[] => {
  if (!Array.isArray(source)) return []
  return source.map(normalizeRemarkImage).filter((item): item is RemarkImageSource => item !== null)
}

const parseDownloadResponseToUrl = (res: any): string => {
  if (res instanceof Blob) {
    return URL.createObjectURL(res)
  }

  const data = res?.response ?? res?.data ?? res
  if (data instanceof Blob) {
    return URL.createObjectURL(data)
  }

  if (typeof data === 'string') {
    if (data.startsWith('blob:') || data.startsWith('data:') || data.startsWith('http')) {
      return data
    }
    return `data:image/jpeg;base64,${data}`
  }

  return ''
}

const revokeImageMapUrls = (imageMap: Map<string, DetailImageItem[]>) => {
  for (const images of imageMap.values()) {
    for (const image of images) {
      if (image.url?.startsWith('blob:')) {
        URL.revokeObjectURL(image.url)
      }
    }
  }
}

const clearRemarkImages = () => {
  revokeImageMapUrls(remarkImagesByMainTable.value)
  remarkImagesByMainTable.value = new Map()
}

const clearOptionExampleImages = () => {
  revokeImageMapUrls(optionExampleImagesByMainTable.value)
  optionExampleImagesByMainTable.value = new Map()
}

const replaceImageItem = (
  imageMapRef: typeof remarkImagesByMainTable | typeof optionExampleImagesByMainTable,
  mainTableId: string,
  fileName: string,
  patch: Partial<DetailImageItem>
) => {
  const currentMap = new Map(imageMapRef.value)
  const currentImages = currentMap.get(mainTableId) || []
  const nextImages = currentImages.map((image) => {
    if (image.fileName !== fileName) return image
    if (image.url?.startsWith('blob:') && patch.url && patch.url !== image.url) {
      URL.revokeObjectURL(image.url)
    }
    return { ...image, ...patch }
  })
  currentMap.set(mainTableId, nextImages)
  imageMapRef.value = currentMap
}

// 加载备注图像
const loadRemarkImages = (imagesByMainTable: Map<string, RemarkImageSource[]>): Promise<void> => {
  if (!imagesByMainTable || imagesByMainTable.size === 0) {
    clearRemarkImages()
    remarkImagesLoadingPromise = Promise.resolve()
    return remarkImagesLoadingPromise
  }

  clearRemarkImages()
  const resultMap = new Map<string, DetailImageItem[]>()
  const tasks: Promise<void>[] = []
  
  for (const [mainTableId, imageList] of imagesByMainTable.entries()) {
    const images = imageList.map((image) => ({
      fileName: image.fileName,
      remark: image.remark,
      url: '',
      loading: true,
      error: false
    }))

    if (images.length > 0) {
      resultMap.set(mainTableId, images)
    }

    imageList.forEach(({ fileName }) => {
      tasks.push(
        list.downloadPhoto({ 文件名: fileName })
          .then((blob) => {
            const url = URL.createObjectURL(blob)
            replaceImageItem(remarkImagesByMainTable, mainTableId, fileName, {
              url,
              loading: false,
              error: false
            })
          })
          .catch((error) => {
            console.error(`加载图片失败: ${fileName}`, error)
            replaceImageItem(remarkImagesByMainTable, mainTableId, fileName, {
              url: '',
              loading: false,
              error: true
            })
          })
      )
    })
  }

  remarkImagesByMainTable.value = resultMap
  remarkImagesLoadingPromise = Promise.allSettled(tasks).then(() => undefined)
  return remarkImagesLoadingPromise
}

const loadOptionExampleImages = (imagesByMainTable: Map<string, string[]>): Promise<void> => {
  if (!imagesByMainTable || imagesByMainTable.size === 0) {
    clearOptionExampleImages()
    return Promise.resolve()
  }

  clearOptionExampleImages()
  const resultMap = new Map<string, DetailImageItem[]>()
  const tasks: Promise<void>[] = []

  for (const [mainTableId, imageList] of imagesByMainTable.entries()) {
    const images = imageList.map((fileName) => ({
      fileName,
      url: '',
      loading: true,
      error: false
    }))

    if (images.length > 0) {
      resultMap.set(mainTableId, images)
    }

    imageList.forEach((fileName) => {
      tasks.push(
        ParameterSettings.clientDownload({ fileName })
          .then((res) => {
            const url = parseDownloadResponseToUrl(res)
            if (!url) throw new Error('图片内容为空')
            replaceImageItem(optionExampleImagesByMainTable, mainTableId, fileName, {
              url,
              loading: false,
              error: false
            })
          })
          .catch((error) => {
            console.error(`加载选项示意图失败: ${fileName}`, error)
            replaceImageItem(optionExampleImagesByMainTable, mainTableId, fileName, {
              url: '',
              loading: false,
              error: true
            })
          })
      )
    })
  }

  optionExampleImagesByMainTable.value = resultMap
  return Promise.allSettled(tasks).then(() => undefined)
}

const addUniqueImageFile = (imageList: string[], fileName: string) => {
  const normalized = toNonEmptyString(fileName)
  if (normalized && !imageList.includes(normalized)) {
    imageList.push(normalized)
  }
}

const parseOptionWithImage = (option: unknown): { label: string; fileName: string } | null => {
  const optionText = toNonEmptyString(option)
  if (!optionText) return null

  const [label, fileName] = optionText.split('|||')
  const normalizedLabel = toNonEmptyString(label)
  const normalizedFileName = toNonEmptyString(fileName)

  if (!normalizedLabel || !normalizedFileName) return null
  return { label: normalizedLabel, fileName: normalizedFileName }
}

const splitSelectedOptionValues = (value: unknown): string[] => {
  const selectedValue = toNonEmptyString(value)
  if (!selectedValue) return []

  return selectedValue
    .split(/[,，;；、|]/)
    .map((item) => toNonEmptyString(item))
    .filter(Boolean)
}

const collectSelectedOptionImages = (project: any, mainTable: any): string[] => {
  const formatDesc = project?.检测原始数据检测数据格式描述
  const optionColumns = Array.isArray(formatDesc?.选项) ? formatDesc.选项 : []
  const subRows = Array.isArray(mainTable?.检测原始数据详细数据子表集合)
    ? mainTable.检测原始数据详细数据子表集合
    : []

  if (optionColumns.length === 0 || subRows.length === 0) return []

  const selectedImages: string[] = []

  optionColumns.forEach((options: unknown, columnIndex: number) => {
    if (!Array.isArray(options) || options.length === 0) return

    const optionImageMap = new Map<string, string>()
    options.forEach((option) => {
      const parsed = parseOptionWithImage(option)
      if (parsed) {
        optionImageMap.set(parsed.label, parsed.fileName)
      }
    })

    if (optionImageMap.size === 0) return

    const fieldKey = `_${columnIndex + 1}`
    subRows.forEach((row: any) => {
      splitSelectedOptionValues(row?.[fieldKey]).forEach((selectedValue) => {
        const fileName = optionImageMap.get(selectedValue)
        if (fileName) {
          addUniqueImageFile(selectedImages, fileName)
        }
      })
    })
  })

  return selectedImages
}

// 加载汇总数据
const loadSummaryData = async () => {
  const id = route.query.id as string
  
  if (!id) {
    ElMessage.error('缺少经营项目ID参数')
    return
  }

  loading.value = true
  try {
    // 调用汇总接口（传入经营项目ID）
    const res = await ParameterSettings.getAllRawDataSummary({ id })

    if (res && (res.status === 200 || res.status === 0) && res.success && res.response) {
      summaryData.value = res.response as Record<string, unknown>
      
      // 从检测原始数据检测项目集合中查找备注图像列表和选项示意图（按主表分组）
      const imagesByMainTable = new Map<string, RemarkImageSource[]>()
      const optionExampleImagesByMainTable = new Map<string, string[]>()
      
      if (res.response['检测原始数据检测项目集合'] && Array.isArray(res.response['检测原始数据检测项目集合'])) {
        const projects = res.response['检测原始数据检测项目集合'] as any[]
        
        // 遍历所有检测项目
        for (const project of projects) {
          if (project['检测原始数据详细数据主表集合'] && Array.isArray(project['检测原始数据详细数据主表集合'])) {
            const mainTables = project['检测原始数据详细数据主表集合'] as any[]
            
            // 遍历所有主表，收集备注图像列表
            for (const mainTable of mainTables) {
              const mainTableId = String(mainTable.Id || mainTable.id)
              let imageList: RemarkImageSource[] = []
              const optionExampleImage = toNonEmptyString(mainTable['选项示意图'] ?? mainTable['选项示意'])
              const optionImageList: string[] = []
              
              if (mainTable['备注图像列表'] && Array.isArray(mainTable['备注图像列表'])) {
                imageList = parseRemarkImages(mainTable['备注图像列表'])
              } else if (mainTable['备注图像列表json']) {
                try {
                  const parsed = JSON.parse(mainTable['备注图像列表json'])
                  if (Array.isArray(parsed)) {
                    imageList = parseRemarkImages(parsed)
                  }
                } catch (e) {
                  console.error('解析备注图像列表json失败:', e)
                }
              }
              
              if (imageList.length > 0) {
                imagesByMainTable.set(mainTableId, imageList)
              }

              if (optionExampleImage) {
                addUniqueImageFile(optionImageList, optionExampleImage)
              }

              collectSelectedOptionImages(project, mainTable).forEach((fileName) => {
                addUniqueImageFile(optionImageList, fileName)
              })

              if (optionImageList.length > 0) {
                optionExampleImagesByMainTable.set(mainTableId, optionImageList)
              }
            }
          }
        }
      }
      
      if (imagesByMainTable.size > 0) {
        remarkImagesLoadingPromise = loadRemarkImages(imagesByMainTable)
      } else {
        clearRemarkImages()
        remarkImagesLoadingPromise = Promise.resolve()
      }

      void loadOptionExampleImages(optionExampleImagesByMainTable)
       
      ElMessage.success('数据加载成功')
    } else {
      ElMessage.error(res?.msg || '加载汇总数据失败')
    }
  } catch (error) {
    console.error('加载汇总数据失败:', error)
    ElMessage.error('加载汇总数据失败')
  } finally {
    loading.value = false
  }
}

// 返回上一页
const goBack = () => {
  router.go(-1)
}

const getExcelColumnLetter = (columnNumber: number): string => {
  let result = ''
  let value = columnNumber
  while (value > 0) {
    const remainder = (value - 1) % 26
    result = String.fromCharCode(65 + remainder) + result
    value = Math.floor((value - 1) / 26)
  }
  return result
}

// 下载Excel图片包
const handleDownloadExcelImages = async () => {
  if (!summaryData.value) {
    ElMessage.warning('暂无汇总数据')
    return
  }

  const projects = summaryData.value['检测原始数据检测项目集合'] as any[]
  if (!projects || projects.length === 0) {
    ElMessage.warning('暂无检测项目数据')
    return
  }

  const imageItems = Array.from(remarkImagesByMainTable.value.values()).flat()
  if (imageItems.some((image) => image.loading)) {
    ElMessage.info('现场图片仍在加载，已等待全部图片加载完成后继续下载')
  }

  downloadingImages.value = true
  try {
    await remarkImagesLoadingPromise
  } catch (error) {
    console.error('等待现场图片加载失败:', error)
    ElMessage.error('现场图片加载失败，无法下载完整图片包')
    downloadingImages.value = false
    return
  }

  const failedImages = Array.from(remarkImagesByMainTable.value.values())
    .flat()
    .filter((image) => image.error || !image.url)
  if (failedImages.length > 0) {
    ElMessage.warning(`有 ${failedImages.length} 张现场图片加载失败，已停止下载，请刷新后重试`)
    downloadingImages.value = false
    return
  }

  // 收集所有需要下载的图片（按显示摘要分组）
  const imageGroups: Array<{ summary: string; images: DetailImageItem[] }> = []
  
  for (const project of projects) {
    const mainTables = project['检测原始数据详细数据主表集合'] as any[]
    if (!mainTables || mainTables.length === 0) continue
    
    for (const mainData of mainTables) {
      const mainTableId = String(mainData.Id || mainData.id)
      const summary = mainData['显示摘要'] || '未命名数据表'
      const images = remarkImagesByMainTable.value.get(mainTableId)
      
      if (images && images.length > 0) {
        // 过滤掉加载失败的图片
        const validImages = images.filter(img => !img.error && !img.loading && img.url)
        if (validImages.length > 0) {
          imageGroups.push({
            summary,
            images: validImages
          })
        }
      }
    }
  }

  if (imageGroups.length === 0) {
    ElMessage.warning('暂无现场照片可下载')
    downloadingImages.value = false
    return
  }

  try {
    ElMessage.info('正在生成Excel文件，请稍候...')

    const [excelJsModule, fileSaverModule] = await Promise.all([
      import('exceljs'),
      import('file-saver')
    ])

    const ExcelJS = excelJsModule.default || excelJsModule
    const saveAs = fileSaverModule.saveAs || fileSaverModule.default?.saveAs || fileSaverModule.default
    if (typeof saveAs !== 'function') {
      throw new Error('file-saver 加载失败')
    }

    // 创建工作簿
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('现场照片')

    // A 列为检测摘要，后续按实际照片数量动态创建“照片 + 备注”列。
    worksheet.getColumn(1).width = 80
    const maxImageCount = Math.max(...imageGroups.map(group => group.images.length))
    for (let imageIndex = 0; imageIndex < maxImageCount; imageIndex++) {
      worksheet.getColumn(imageIndex * 2 + 2).width = 18
      worksheet.getColumn(imageIndex * 2 + 3).width = 26
    }

    let currentRow = 1

    // 遍历每个分组
    for (const group of imageGroups) {
      // 添加显示摘要标题
      const titleCell = worksheet.getCell(`A${currentRow}`)
      titleCell.value = group.summary
      titleCell.font = { bold: true, size: 12 }
      titleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }
      
      // 设置行高
      worksheet.getRow(currentRow).height = 90

      // 添加图片及其右侧备注，图片数量不设上限。
      for (let i = 0; i < group.images.length; i++) {
        const image = group.images[i]
        const imageColumnLetter = getExcelColumnLetter(i * 2 + 2)
        const remarkColumnLetter = getExcelColumnLetter(i * 2 + 3)

        const remark = image.remark?.trim()
        const remarkCell = worksheet.getCell(`${remarkColumnLetter}${currentRow}`)
        remarkCell.value = `备注：${remark || '无'}`
        remarkCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }

        try {
          // 将blob URL转换为ArrayBuffer
          const response = await fetch(image.url)
          const blob = await response.blob()
          const arrayBuffer = await blob.arrayBuffer()
          
          // 添加图片到工作簿
          const imageId = workbook.addImage({
            buffer: arrayBuffer,
            extension: 'jpeg'
          })

          // 使用单元格引用方式插入图片（确保图片在指定列内）
          worksheet.addImage(imageId, `${imageColumnLetter}${currentRow}:${imageColumnLetter}${currentRow}`)
        } catch (error) {
          console.error(`添加图片失败: ${image.fileName}`, error)
        }
      }

      currentRow++
    }

    // 生成Excel文件
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    })
    
    // 下载文件
    const fileName = `${businessInfo.value?.name || '原始数据汇总'}_现场照片_${new Date().getTime()}.xlsx`
    saveAs(blob, fileName)
    
    ElMessage.success('Excel图片包下载成功')
  } catch (error) {
    console.error('下载Excel图片包失败:', error)
    ElMessage.error('下载Excel图片包失败')
  } finally {
    downloadingImages.value = false
  }
}

onMounted(() => {
  loadSummaryData()
})

onBeforeUnmount(() => {
  clearRemarkImages()
  clearOptionExampleImages()
})
</script>

<style scoped>
.summary-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.back-button {
  margin-right: 16px;
}

.loading-container {
  background: #fff;
  padding: 40px;
  border-radius: 8px;
  margin-top: 20px;
}

.detail-content {
  margin-top: 20px;
}

.info-card {
  margin-bottom: 20px;
}

.info-tags-container {
  padding: 10px 0;
}

.info-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.info-tag {
  padding: 12px 20px;
  font-size: 14px;
  border-radius: 6px;
}

.tag-label {
  font-weight: 600;
  margin-right: 8px;
}

.tag-value {
  font-weight: normal;
}

.projects-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.project-card {
  background: #fff;
}

/* 特殊属性样式 */
.special-attributes {
  margin-bottom: 20px;
}

.special-attributes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attribute-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.attribute-label {
  font-weight: 500;
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
  min-width: 100px;
}

.attribute-value {
  display: inline-block;
  min-width: 200px;
  padding: 8px 12px;
  background-color: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  color: #303133;
  font-size: 14px;
}

/* 详细数据样式 */
.detailed-data {
  margin-top: 20px;
}

.detailed-data h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  padding-left: 12px;
  border-left: 4px solid #409eff;
}

.data-table-container {
  margin-bottom: 24px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
}

.table-summary-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  /* background-color: #f5f7fa; */
  border-bottom: 1px solid #ebeef5;
}

.table-title {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}

.table-summary {
  flex: 0 1 auto;
  min-width: 0;
  color: #606266;
  font-size: 13px;
}

.option-example-images {
  max-width: 360px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
  flex: 0 0 auto;
}

.option-example-image {
  width: 96px;
  height: 72px;
  flex: 0 0 96px;
}

.option-example-thumb {
  width: 96px;
  height: 72px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  cursor: zoom-in;
}

.option-example-thumb :deep(.el-image__inner) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.option-example-placeholder {
  width: 96px;
  height: 72px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  color: #909399;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.data-table {
  width: 100%;
}

.data-table :deep(.divider-column) {
  border-right: 2px solid #409eff !important;
}

.data-table :deep(.el-table__cell) {
  padding: 8px 0;
}

.data-table :deep(.el-table__header-wrapper th) {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.remark-image-preview-dialog {
  display: flex;
  flex-direction: column;
  min-height: 420px;
}

.remark-image-preview-stage {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 16px 64px;
  background: #f5f7fa;
}

.remark-image-preview-image {
  display: block;
  max-width: 100%;
  max-height: min(64vh, 640px);
  object-fit: contain;
}

.remark-preview-nav {
  position: absolute;
  z-index: 1;
}

.remark-preview-nav--previous {
  left: 16px;
}

.remark-preview-nav--next {
  right: 16px;
}

.remark-image-preview-note {
  position: sticky;
  bottom: 0;
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
  min-height: 52px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-top: 0;
  line-height: 24px;
}

.remark-image-preview-note-label {
  flex: 0 0 auto;
  color: #606266;
  font-weight: 600;
}

.remark-image-preview-note-value {
  min-width: 0;
  color: #303133;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* 主表内的现场照片样式 */
.main-table-images {
  margin-top: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 2px solid #303133;
}

.main-table-images h5 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.images-grid-small {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.image-item-small {
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.image-item-small:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.thumbnail-image-small {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.thumbnail-image-small :deep(.el-image__inner) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-error-small {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 10px;
}

.image-error-small .el-icon {
  font-size: 24px;
}

.image-loading-small {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f7fa;
  color: #409eff;
}

.image-loading-small .el-icon {
  font-size: 24px;
}

.image-error-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 10px;
  border-radius: 4px;
}

.image-error-placeholder .el-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.image-error-placeholder span {
  font-size: 10px;
}
</style>
